/**
 * 경기 지도 — 17개 시 SVG를 캔버스에 비율 좌표로 합성.
 *
 * 디자인 의도:
 *  - 시별로 SVG 분리 (디자이너가 export). 각 시 path는 디자이너가 동 단위 가용성까지 색으로 표현.
 *  - 시 라벨은 SVG 외부에 별도 텍스트 (작은 시는 path 영역 밖)
 *  - 라벨 클릭 → page.tsx의 onLabelClick(sigunguName) 호출 (펼침 + 스크롤은 page에서 처리)
 *  - 필터(전체/북부/남부)는 page.tsx의 selectedSubRegion에서 결정 → subRegion prop으로 받음
 *
 * 반응형:
 *  - 좌표는 px (필터별 캔버스 기준) — 본 컴포넌트에서 % 단위로 변환해 inline style 주입
 *  - MapStage가 width: 100% / max-width: canvas.width / aspect-ratio 동적 → viewport 따라 자동 스케일
 *  - 라벨 폰트는 cqw 기반 → stage 크기 따라 자동
 *
 * 데이터/좌표는 `constants/gyeonggiRegions.ts` 참조.
 */

import { Fragment, useMemo } from 'react'

import {
  GYEONGGI_BY_FILTER,
  GYEONGGI_CODE_TO_SIGUNGU_NAME,
  GYEONGGI_RENDER_SCALE,
  type GyeonggiFilter,
} from '../../../constants/gyeonggiRegions'

import { MapStage, RegionImage, RegionLabel, RegionLabelHitArea, Wrapper } from './GyeonggiMap.styles'

interface GyeonggiMapProps {
  /** 백엔드 subRegion 한글값. null/undefined면 전체 캔버스 */
  subRegion?: string | null
  /**
   * 시 라벨 클릭 시 호출. 인자는 백엔드 SiGunGu.name(한글, 예: "수원시").
   * page.tsx가 받아서 해당 시군구 카드 펼침 + 스크롤 처리.
   */
  onLabelClick?: (sigunguName: string) => void
}

/** 백엔드 subRegion 한글 → 내부 캔버스 키 매핑. 모르는 값이면 'full'로 fallback */
const SUB_REGION_TO_FILTER: Record<string, GyeonggiFilter> = {
  '경기 북부': 'north',
  '경기 남부': 'south',
}

export default function GyeonggiMap({ subRegion, onLabelClick }: GyeonggiMapProps) {
  const filter: GyeonggiFilter = subRegion ? (SUB_REGION_TO_FILTER[subRegion] ?? 'full') : 'full'
  // 백엔드가 신규 subRegion을 추가했는데 클라 매핑이 없는 경우 사일런트로 'full' 폴백 — dev에서 경고로 감지
  if (import.meta.env.DEV && subRegion && !SUB_REGION_TO_FILTER[subRegion]) {
    console.warn(`[GyeonggiMap] 매핑되지 않은 subRegion: "${subRegion}" → 'full' 캔버스로 폴백`)
  }
  const { canvas, regions } = GYEONGGI_BY_FILTER[filter]

  /** px 좌표 → 현재 필터 캔버스 기준 % 문자열 */
  const toPercent = useMemo(
    () => ({
      x: (px: number) => `${(px / canvas.width) * 100}%`,
      y: (px: number) => `${(px / canvas.height) * 100}%`,
    }),
    [canvas]
  )

  const handleLabelClick = (regionCode: string) => {
    const sigunguName = GYEONGGI_CODE_TO_SIGUNGU_NAME[regionCode]
    if (!sigunguName) {
      if (import.meta.env.DEV) {
        console.warn(`[GyeonggiMap] 매핑 없는 region code: "${regionCode}"`)
      }
      return
    }
    onLabelClick?.(sigunguName)
  }

  return (
    <Wrapper>
      <MapStage
        style={{
          // canvas.width 는 좌표 기준 (% 변환에 사용) — 유지
          // 시각 사이즈만 GYEONGGI_RENDER_SCALE 곱해서 축소
          maxWidth: canvas.width * GYEONGGI_RENDER_SCALE,
          aspectRatio: `${canvas.width} / ${canvas.height}`,
        }}>
        {/*
         * region 단위로 [라벨 button, path img] sibling 배치.
         * styles의 button:active + img selector가 이 순서를 의존 — 순서 바꾸지 말 것.
         * z-index는 styles에서: button(2) > img(1).
         */}
        {regions.map((region, idx) => (
          <Fragment key={`region-${idx}`}>
            <RegionLabelHitArea
              type='button'
              aria-label={`${region.name} 영역 보기`}
              onClick={() => handleLabelClick(region.code)}
              style={{
                left: toPercent.x(region.label.x),
                top: toPercent.y(region.label.y),
              }}>
              <RegionLabel data-canvas={filter}>{region.name}</RegionLabel>
            </RegionLabelHitArea>
            <RegionImage
              src={region.svgUrl}
              alt=''
              style={{
                left: toPercent.x(region.x),
                top: toPercent.y(region.y),
                width: toPercent.x(region.width),
              }}
            />
          </Fragment>
        ))}
      </MapStage>
    </Wrapper>
  )
}
